import { useState } from "react";
import { motion } from "framer-motion";
import {
  CaretDown,
  Brain,
  ListChecks,
  ChatCircleDots,
  BookOpen,
} from "@phosphor-icons/react";
import { cn } from "../../../lib/cn";

type SectionId = "suggestions" | "rubric" | "feedback" | "materials";

const SECTIONS: { id: SectionId; label: string; icon: typeof Brain; count: number }[] = [
  { id: "suggestions", label: "Suggestions", icon: Brain, count: 3 },
  { id: "rubric", label: "Rubric", icon: ListChecks, count: 4 },
  { id: "feedback", label: "Past Feedback", icon: ChatCircleDots, count: 2 },
  { id: "materials", label: "Materials", icon: BookOpen, count: 3 },
];

export function DemoContextPanel() {
  const [openSections, setOpenSections] = useState<Set<SectionId>>(
    () => new Set(["suggestions", "rubric"]),
  );

  const toggle = (id: SectionId) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-0.5">
      {/* Course header — matches real ContextPanel */}
      <div className="px-3 py-2 border-b border-[var(--border-hairline)]">
        <p className="text-[11px] font-semibold text-[var(--text-primary)]">
          CS 229 &mdash; Problem Set 3
        </p>
        <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
          Machine Learning &middot; Prof. Andrew Ng
        </p>
      </div>

      {/* Collapsible sections — matches real ContextPanel toggle pattern */}
      {SECTIONS.map(({ id, label, icon: Icon, count }) => {
        const isOpen = openSections.has(id);
        return (
          <div key={id}>
            <button
              onClick={() => toggle(id)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-1.5",
                "text-[11px] font-medium text-[var(--text-secondary)]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors",
              )}
            >
              <Icon size={12} weight="bold" />
              <span className="flex-1 text-left">{label}</span>
              {count > 0 && (
                <span className="text-[9px] font-mono text-[var(--text-muted)] bg-[var(--bg-surface-hover)] px-1.5 rounded-full">
                  {count}
                </span>
              )}
              <motion.span
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.15 }}
              >
                <CaretDown size={10} weight="bold" className="text-[var(--text-muted)]" />
              </motion.span>
            </button>

            {isOpen && (
              <div className="px-3 py-1.5">
                {id === "suggestions" && <SuggestionsContent />}
                {id === "rubric" && <RubricContent />}
                {id === "feedback" && <FeedbackContent />}
                {id === "materials" && <MaterialsContent />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SuggestionsContent() {
  const items = [
    "Strengthen the thesis statement in the introduction",
    "Add quantitative comparisons to the results section",
    "Include a limitations paragraph before the conclusion",
  ];
  return (
    <div className="flex flex-col gap-1">
      {items.map((s, i) => (
        <div
          key={i}
          className="flex items-start gap-2 rounded-[var(--radius-sm)] bg-[var(--color-lever-orange)]/5 border border-[var(--color-lever-orange)]/10 px-2 py-1.5"
        >
          <Brain size={11} weight="fill" className="text-[var(--color-lever-orange)] shrink-0 mt-0.5" />
          <span className="text-[10px] text-[var(--text-secondary)] leading-relaxed">{s}</span>
        </div>
      ))}
    </div>
  );
}

function RubricContent() {
  const items = [
    { label: "Abstract (200 words max)", done: true },
    { label: "Methodology section with formal definitions", done: true },
    { label: "Experimental results with tables/figures", done: false },
    { label: "Discussion of limitations and future work", done: false },
  ];
  return (
    <div className="flex flex-col gap-0.5">
      {items.map((item, i) => (
        <label
          key={i}
          className="flex items-center gap-2 rounded-[var(--radius-sm)] px-1.5 py-1 hover:bg-[var(--bg-surface-hover)] transition-colors cursor-default"
        >
          <div
            className={cn(
              "h-3.5 w-3.5 rounded-[3px] border flex items-center justify-center shrink-0",
              item.done
                ? "bg-[var(--color-signal-green)] border-[var(--color-signal-green)]"
                : "border-[var(--border-default)] bg-transparent",
            )}
          >
            {item.done && (
              <svg width={8} height={8} viewBox="0 0 8 8" fill="none">
                <path d="M1.5 4L3.2 5.7L6.5 2.3" stroke="white" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <span
            className={cn(
              "text-[10px]",
              item.done
                ? "text-[var(--text-muted)] line-through"
                : "text-[var(--text-secondary)]",
            )}
          >
            {item.label}
          </span>
        </label>
      ))}
    </div>
  );
}

function FeedbackContent() {
  const patterns = [
    "Your introductions tend to lack a clear problem statement. Be more direct about the research gap.",
    "Good use of figures in past submissions — keep including visual explanations of your approach.",
  ];
  return (
    <div className="flex flex-col gap-1">
      {patterns.map((p, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-sm)] bg-[var(--bg-surface)] border border-[var(--border-hairline)] px-2 py-1.5"
        >
          <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">{p}</p>
        </div>
      ))}
    </div>
  );
}

function MaterialsContent() {
  const files = [
    { name: "lecture-notes-week8.pdf", summary: "Covers SVMs, kernel methods, and regularization" },
    { name: "bishop-ch4-ml.pdf", summary: "Linear models for classification" },
    { name: "assignment-rubric.pdf", summary: "Grading criteria and formatting requirements" },
  ];
  return (
    <div className="flex flex-col gap-1">
      {files.map((m) => (
        <div
          key={m.name}
          className="rounded-[var(--radius-sm)] bg-[var(--bg-surface)] border border-[var(--border-hairline)] px-2 py-1.5"
        >
          <p className="text-[10px] font-medium text-[var(--text-primary)]">{m.name}</p>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5 line-clamp-2">{m.summary}</p>
        </div>
      ))}
    </div>
  );
}
